import type { TOC } from '@ember/component/template-only';
import { fn, get } from '@ember/helper';
import { on } from '@ember/modifier';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert } from '@ember/render-modifiers';
import {
  PrismCard,
  PrismComboSelectInput,
  PrismIcon,
  PrismLoadingBar,
  PrismMessage,
  PrismSelectInput,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { and } from 'ember-truth-helpers';
import GeneralIconWithTooltip from 'lumen/components/general/icon-with-tooltip';
import GeneralNavigationIntercomHelpLink from 'lumen/components/general/navigation/intercom-help-link';
import GeneralPageHeader from 'lumen/components/general/page/header';
import GeneralPageMain from 'lumen/components/general/page/main';
import HotelStrategyChartsChartDayOfWeek from 'lumen/components/hotel/strategy/charts/chart-day-of-week';
import HotelStrategyChartsChartLeadtimePattern from 'lumen/components/hotel/strategy/charts/chart-leadtime-pattern';
import HotelStrategyChartsChartRoomtypePattern from 'lumen/components/hotel/strategy/charts/chart-roomtype-pattern';
import HotelStrategyCompsetPosition from 'lumen/components/hotel/strategy/compset-position';
import HotelStrategyPosTypeCard from 'lumen/components/hotel/strategy/pos-type-card';
import HotelStrategyTypeCard from 'lumen/components/hotel/strategy/type-card';
import HotelAndMarketDropdownsAccountSelectorWrapper from 'lumen/components/hotel-and-market/dropdowns/account-selector-wrapper';
import type HotelStrategyController from 'lumen/controllers/hotel/strategy';
import can from 'lumen/helpers/can';
import hasFeature from 'lumen/helpers/has-feature';
import {
  TYPE_ID_DOW,
  TYPE_ID_LEADTIME,
  TYPE_ID_ROOMTYPE,
} from 'lumen/utils/constants/rate-strategy-constants';

interface HotelStrategySignature {
  Args: {
    controller: HotelStrategyController;
    model: unknown;
  };
}

<template>
  {{! @glint-nocheck: Need to convert controller to *.ts }}
  {{! template-lint-disable require-presentational-children }}
  <GeneralPageHeader
    class="rate-strategy"
    {{didInsert @controller.initRsSelections}}
  >
    <div class="content-wrapper flex flex-row items-center">
      <div class="flex gap-400">
        <HotelAndMarketDropdownsAccountSelectorWrapper @hasDivider={{true}} />
        {{@controller.formattedDataRangeText}}
      </div>
      <PrismComboSelectInput
        @buildLabelWithCount={{@controller.competitorsBuildLabelWithCount}}
        @onChange={{@controller.handleCompetitorsChange}}
        @options={{@controller.competitorsPerCompset}}
        @selectAllLabel={{t "general.competitors.count.any"}}
        @unsafe_dropdownClass="multi-select-competitors"
        @value={{@controller.competitorsValue}}
      />

      <div class="select-wrapper">
        <div class="no-filter-content">
          {{t
            "rates.nights"
            count=(get
              @controller.hotelAndMarketService.hotel.defaultLosRates "0"
            )
          }}
        </div>
      </div>
    </div>
  </GeneralPageHeader>

  <GeneralPageMain>
    {{#if @controller.isLoading}}
      <PrismLoadingBar @isFixed={{true}} @positionTop={{120}} />
    {{/if}}

    {{#if @controller.showFullPageEmptyState}}
      <div
        class="rate-strategy-empty-state-container flex items-center justify-center"
      >
        <PrismMessage
          @description={{t "general.empty_state.text.busy_calculating"}}
          @illustration="100x100_progress"
          @title={{t "general.empty_state.title.crunching_numbers"}}
        />
      </div>
    {{else}}
      <div class="rate-strategy content-wrapper main-content">
        <div class="section-title mt-0">
          <div class="flex items-center justify-between">
            <h2 class="text-neutral-emphasis mr-200 flex items-center">
              <div>
                {{t "strategy.section_title.positioning"}}
                <GeneralNavigationIntercomHelpLink
                  @articleLink="en/articles/5296064-rate-strategy-part-1-positioning"
                  @iconName="circle-question-mark"
                  @type="icon"
                />
              </div>
            </h2>
          </div>
        </div>
        <div class="flex flex-row">
          {{#if @controller.isLoading}}
            <PrismCard
              @contentLayout="noPadding"
              class="rate-strategy-card rate-strategy-card__compset-pos"
            >
              <:header>
                <div class="flex flex-row items-center">
                  <p class="segmentation-value-loading-state-bar"></p>
                </div>
              </:header>
            </PrismCard>
          {{else}}
            <PrismCard
              @contentLayout="noPadding"
              class="rate-strategy-card rate-strategy-card__compset-pos"
            >
              <:header as |Header|>
                <div class="flex flex-row items-center gap-100">
                  <Header.title>
                    {{t "strategy.block_title.rate_position_in_compset"}}
                  </Header.title>
                  <GeneralIconWithTooltip
                    @side="top"
                    @size="small"
                    @tooltipContent={{t
                      "strategy.block_title_explanation.compset_positioning_per_strategy"
                    }}
                  />
                </div>
              </:header>
              <:content>
                <div class="compset-position-block__content p-400 pt-0">
                  <HotelStrategyCompsetPosition
                    @subtypesWithStrategy={{@controller.subtypesWithStrategy}}
                    @switchToDetail={{@controller.switchToDetailViaCompsetPosPerStrategy}}
                  />
                </div>
              </:content>
            </PrismCard>
          {{/if}}
          <PrismCard
            class="rate-strategy-card rate-strategy-card--chart rate-strategy-card__dow"
          >
            <:header as |Header|>
              <div
                class="button--unstyled flex items-center justify-between"
                role="button"
                {{on
                  "click"
                  (fn @controller.switchToDetailViaTitleExpand TYPE_ID_DOW)
                }}
              >
                {{#if @controller.isLoading}}
                  <p class="segmentation-value-loading-state-bar"></p>
                {{else}}
                  <div class="flex items-center justify-start gap-200">
                    <Header.title
                    >{{@controller.typesDataPerId.day_of_week.formattedTypeName}}</Header.title>
                    <GeneralIconWithTooltip
                      @side="top"
                      @size="small"
                      @tooltipContent={{t
                        "strategy.block_title_explanation.positioning_per_day_of_week"
                      }}
                    />
                  </div>
                {{/if}}
                <span class="ic-product-tour-rate-strategy-expand">
                  <PrismIcon @name="expand" />
                </span>
              </div>
            </:header>

            <:content>
              <div class="overflow-hidden">
                {{#if @controller.hasTypesData}}
                  <HotelStrategyChartsChartDayOfWeek
                    @data={{@controller.typesDataPerId.day_of_week}}
                    @isDetailView={{false}}
                  />
                {{/if}}
              </div>
            </:content>
          </PrismCard>
        </div>
        <div class="section-title">
          <div class="flex items-center justify-between">

            <h2 class="text-neutral-emphasis mr-200 flex items-center">
              <div>
                {{t "strategy.section_title.promotion_pricing_strategies"}}
                <GeneralNavigationIntercomHelpLink
                  @articleLink="en/articles/5296110-rate-strategy-part-2-promotion-pricing-strategies"
                  @iconName="circle-question-mark"
                  @type="icon"
                />
              </div>
            </h2>
            <div class="section-title-group text-neutral-subdued ml-300">
              <PrismSelectInput
                @horizontalPosition="right"
                @onChange={{@controller.updateReferencePeriodForStrategySection}}
                @options={{@controller.referencePeriodKeys}}
                @value={{@controller.selectedReferencePeriodForStrategySection}}
              />
            </div>
          </div>
        </div>
        <div class="rate-strategy-row-with-dynamic-cards">
          {{#if
            (and
              @controller.typesDataPerId.los
              (hasFeature "rate_strategy.blocks.los")
            )
          }}
            <HotelStrategyTypeCard
              @data={{@controller.typesDataPerId.los}}
              @isLoading={{@controller.isLoading}}
              @nbOfRowsInCard={{2}}
              @switchToDetailViaPositionButton={{@controller.switchToDetailViaPositionButton}}
              @switchToDetailViaTitleExpand={{@controller.switchToDetailViaTitleExpand}}
            />
          {{/if}}

          {{#if @controller.typesDataPerId.mobile}}
            <HotelStrategyTypeCard
              @data={{@controller.typesDataPerId.mobile}}
              @isLoading={{@controller.isLoading}}
              @nbOfRowsInCard={{2}}
              @switchToDetailViaPositionButton={{@controller.switchToDetailViaPositionButton}}
              @switchToDetailViaTitleExpand={{@controller.switchToDetailViaTitleExpand}}
            />
          {{/if}}

          {{#if @controller.typesDataPerId.mealtype}}
            <HotelStrategyTypeCard
              @data={{@controller.typesDataPerId.mealtype}}
              @isLoading={{@controller.isLoading}}
              @nbOfRowsInCard={{2}}
              @switchToDetailViaPositionButton={{@controller.switchToDetailViaPositionButton}}
              @switchToDetailViaTitleExpand={{@controller.switchToDetailViaTitleExpand}}
            />
          {{/if}}

          {{#if @controller.typesDataPerId.pos}}
            <HotelStrategyPosTypeCard
              @canEditPOS={{can "settings.compset.edit"}}
              @data={{@controller.typesDataPerId.pos}}
              @isLoading={{@controller.isLoading}}
              @nbOfRowsInCard={{2}}
              @redirectAction={{@controller.redirectToPosSettings}}
              @switchToDetailViaPositionButton={{@controller.switchToDetailViaPositionButton}}
              @switchToDetailViaTitleExpand={{@controller.switchToDetailViaTitleExpand}}
            />
          {{/if}}
          {{#if @controller.typesDataPerId.membership}}
            <HotelStrategyTypeCard
              @data={{@controller.typesDataPerId.membership}}
              @isLoading={{@controller.isLoading}}
              @nbOfRowsInCard={{2}}
              @switchToDetailViaPositionButton={{@controller.switchToDetailViaPositionButton}}
              @switchToDetailViaTitleExpand={{@controller.switchToDetailViaTitleExpand}}
            />
          {{/if}}
        </div>

        <div class="section-title">
          <div class="flex items-center justify-between">
            <h2 class="text-neutral-emphasis mr-200 flex items-center">
              <div>
                {{t "strategy.section_title.cancellation_policy"}}
                <GeneralNavigationIntercomHelpLink
                  @articleLink="en/articles/5296158-rate-strategy-part-3-cancellation-policies"
                  @iconName="circle-question-mark"
                  @type="icon"
                />
              </div>
            </h2>
            <div class="section-title-group text-neutral-subdued ml-300">
              <PrismSelectInput
                @horizontalPosition="right"
                @onChange={{@controller.updateReferencePeriodForCancellationPolicySection}}
                @options={{@controller.referencePeriodKeys}}
                @value={{@controller.selectedReferencePeriodForCancellationPolicySection}}
              />
            </div>
          </div>
        </div>

        <div class="rate-strategy-row-with-dynamic-cards">
          {{#if @controller.typesDataPerId.membership}}
            <HotelStrategyTypeCard
              @data={{@controller.typesDataPerId.best_flex}}
              @isLoading={{@controller.isLoading}}
              @nbOfRowsInCard={{1}}
              @switchToDetailViaPositionButton={{@controller.switchToDetailViaPositionButton}}
              @switchToDetailViaTitleExpand={{@controller.switchToDetailViaTitleExpand}}
            />
          {{/if}}

          {{#if @controller.typesDataPerId.semi_flex}}
            <HotelStrategyTypeCard
              @data={{@controller.typesDataPerId.semi_flex}}
              @isLoading={{@controller.isLoading}}
              @nbOfRowsInCard={{1}}
              @switchToDetailViaPositionButton={{@controller.switchToDetailViaPositionButton}}
              @switchToDetailViaTitleExpand={{@controller.switchToDetailViaTitleExpand}}
            />
          {{/if}}

          {{#if @controller.typesDataPerId.non_ref}}
            <HotelStrategyTypeCard
              @data={{@controller.typesDataPerId.non_ref}}
              @isLoading={{@controller.isLoading}}
              @nbOfRowsInCard={{1}}
              @switchToDetailViaPositionButton={{@controller.switchToDetailViaPositionButton}}
              @switchToDetailViaTitleExpand={{@controller.switchToDetailViaTitleExpand}}
            />
          {{/if}}
        </div>

        <div class="section-title">
          <div class="flex items-center justify-between">
            <h2 class="text-neutral-emphasis mr-200 flex items-center">
              <div>
                {{t "strategy.section_title.pricing_patterns"}}
                <GeneralNavigationIntercomHelpLink
                  @articleLink="en/articles/5296161-rate-strategy-part-4-pricing-patterns"
                  @iconName="circle-question-mark"
                  @type="icon"
                />
              </div>
            </h2>
          </div>
        </div>

        <div class="flex flex-row">
          <PrismCard
            class="rate-strategy-card ic-product-tour-rate-strategy-card__leadtime rate-strategy-card--chart w-1/2"
          >
            <:header as |Header|>
              <div
                class="button--unstyled flex items-center justify-between"
                role="button"
                {{on
                  "click"
                  (fn @controller.switchToDetailViaTitleExpand TYPE_ID_LEADTIME)
                }}
              >
                {{#if @controller.isLoading}}
                  <p class="segmentation-value-loading-state-bar"></p>
                {{else}}
                  <div class="flex items-center justify-start gap-200">
                    <Header.title>
                      {{@controller.typesDataPerId.leadtime.formattedTypeName}}
                    </Header.title>
                    <GeneralIconWithTooltip
                      @side="top"
                      @size="small"
                      @tooltipContent={{t
                        "strategy.block_title_explanation.leadtime_pattern"
                      }}
                    />
                  </div>
                {{/if}}
                <span class="ic-product-tour-rate-strategy-expand">
                  <PrismIcon @name="expand" />
                </span>
              </div>
            </:header>

            <:content>
              {{#if @controller.hasTypesData}}
                <HotelStrategyChartsChartLeadtimePattern
                  @data={{@controller.typesDataPerId.leadtime}}
                  @isDetailView={{false}}
                />
              {{/if}}
            </:content>
          </PrismCard>

          <PrismCard
            class="rate-strategy-card ic-product-tour-rate-strategy-card__roomtype rate-strategy-card--chart w-1/2"
          >
            <:header as |Header|>
              <div
                class="button--unstyled flex items-center justify-between"
                role="button"
                {{on
                  "click"
                  (fn @controller.switchToDetailViaTitleExpand TYPE_ID_ROOMTYPE)
                }}
              >
                {{#if @controller.isLoading}}
                  <p class="segmentation-value-loading-state-bar"></p>
                {{else}}
                  <div class="flex items-center justify-start gap-200">
                    <Header.title>
                      {{@controller.typesDataPerId.roomtype.formattedTypeName}}
                    </Header.title>
                  </div>
                {{/if}}
                <span class="ic-product-tour-rate-strategy-expand">
                  <PrismIcon @name="expand" />
                </span>
              </div>
            </:header>

            <:content>
              {{#if @controller.hasTypesData}}
                <HotelStrategyChartsChartRoomtypePattern
                  @data={{@controller.typesDataPerId.roomtype}}
                  @isDetailView={{false}}
                />
              {{/if}}
            </:content>
          </PrismCard>
        </div>
      </div>
    {{/if}}
  </GeneralPageMain>

  {{outlet}}
</template> satisfies TOC<HotelStrategySignature>;
